/**
 * BTC3 Phase 5 · "Talk to Someone Now" action (D4).
 *
 * Reuses the EXISTING on-call capability (`POST /api/buddy/on-call`).
 * No parallel support/CRM/ticket system is created.  The on-call
 * interaction is independent of the Buddy connection lifecycle:
 *   • does NOT create a Buddy connection
 *   • does NOT consume a Buddy slot
 *   • does NOT alter buddy_matched / buddy_connected / activated.
 *
 * Phase 5 D4 — truthful empty/waiting states.  No fabricated Buddy
 * profile, name or photo is rendered anywhere in this flow.
 */
import { useState } from "react";
import { api } from "../lib/auth";
import { Phone } from "lucide-react";

const DEFAULT_DURATION = 45;

export default function TalkToSomeoneNow({ className = "" }) {
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [reason, setReason] = useState("");

  const request = async () => {
    setBusy(true);
    setError(null);
    setStatus(null);
    try {
      const { data } = await api.post(`/buddy/on-call`, {
        duration_minutes: DEFAULT_DURATION,
        reason: (reason || "I'd like to talk to someone.").slice(0, 1024),
      });
      setStatus(data?.session?.status || "requested");
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
          "We couldn't start an on-call request just now.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="talk-to-someone-now"
      className={`rounded-2xl bg-white border border-gray-100 p-5 ${className}`}
    >
      <div className="flex items-start gap-3">
        <Phone className="w-5 h-5 text-[#23803F] shrink-0 mt-0.5" />
        <div className="flex-1">
          <p
            className="font-semibold text-[#0B132B] text-sm"
            data-testid="talk-to-someone-now-heading"
          >
            Talk to Someone Now
          </p>
          <p
            className="text-xs text-[#4B5563] mt-0.5"
            data-testid="talk-to-someone-now-copy"
          >
            Not your Buddy — a BTC on-call volunteer who can listen
            while you wait for a match.
          </p>
          {status ? (
            <p
              data-testid="talk-to-someone-now-status"
              role="status"
              aria-live="polite"
              className="mt-3 text-xs text-[#23803F]"
            >
              On-call request started · {status}
            </p>
          ) : (
            <>
              <label htmlFor="talk-to-someone-now-reason" className="sr-only">
                What should the on-call volunteer know (optional)
              </label>
              <textarea
                id="talk-to-someone-now-reason"
                data-testid="talk-to-someone-now-reason"
                className="mt-3 w-full border border-gray-200 rounded-md text-xs p-2 focus:border-[#23803F] focus:outline-none focus:ring-2 focus:ring-[#23803F]/20"
                placeholder="Anything the on-call person should know (optional)"
                rows={2}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                disabled={busy}
                maxLength={1024}
                aria-label="What should the on-call volunteer know (optional)"
              />
              <button
                data-testid="talk-to-someone-now-button"
                onClick={request}
                disabled={busy}
                className="mt-2 inline-flex items-center gap-2 text-xs font-semibold text-white bg-[#23803F] hover:bg-[#1A6632] disabled:opacity-60 rounded-md px-3 py-1.5 focus-visible:outline-2 focus-visible:outline focus-visible:outline-[#0B132B] focus-visible:outline-offset-2"
              >
                {busy ? "Starting…" : "Start on-call"}
              </button>
            </>
          )}
          {error && (
            <p
              data-testid="talk-to-someone-now-error"
              role="alert"
              className="mt-2 text-xs text-[#B00020]"
            >
              {error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
